import { memo } from 'react';
import { BarChart3, TrendingUp, Target, Award } from 'lucide-react';

/**
 * StatisticsPanel Component
 * Displays detailed statistics and learning insights
 * Memoized to prevent unnecessary re-renders
 */
const StatisticsPanel = memo(function StatisticsPanel({
  statistics,
  getAccuracyPercentage,
  getOperationAccuracy,
  getDifficultyAccuracy,
  getWeakestOperation,
  getSuggestedDifficulty,
}) {
  if (!statistics || statistics.totalProblems === 0) {
    return (
      <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
        <div className="text-6xl mb-4">📊</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Ingen statistik endnu
        </h2>
        <p className="text-gray-600">
          Løs nogle opgaver for at se din fremgang!
        </p>
      </div>
    );
  }

  const overallAccuracy = getAccuracyPercentage();
  const weakest = getWeakestOperation();
  const suggestion = getSuggestedDifficulty();

  // Operation names in Danish
  const operationNames = {
    plus: 'Plus',
    minus: 'Minus',
    gange: 'Gange',
  };

  // Difficulty names in Danish
  const difficultyNames = {
    let: 'Let',
    medium: 'Medium',
    svært: 'Svært',
  };

  return (
    <div className="space-y-6">
      {/* Overall Stats */}
      <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="text-purple-600" size={32} />
          <h2 className="text-2xl font-bold text-gray-800">Samlet Statistik</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-purple-600">
              {statistics.totalProblems}
            </div>
            <div className="text-sm text-gray-600">Opgaver</div>
          </div>

          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-green-600">
              {statistics.correctAnswers}
            </div>
            <div className="text-sm text-gray-600">Rigtige</div>
          </div>

          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-red-600">
              {statistics.wrongAnswers}
            </div>
            <div className="text-sm text-gray-600">Forkerte</div>
          </div>

          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">
              {overallAccuracy}%
            </div>
            <div className="text-sm text-gray-600">Præcision</div>
          </div>
        </div>
      </div>

      {/* Operation Breakdown */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <Target className="text-blue-600" size={28} />
          <h3 className="text-xl font-bold text-gray-800">
            Præstation pr. regneart
          </h3>
        </div>

        <div className="space-y-3">
          {['plus', 'minus', 'gange'].map((op) => {
            const opStats = getOperationAccuracy(op);
            if (opStats.total === 0) return null;

            return (
              <div key={op} className="bg-gray-50 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-700">
                    {operationNames[op]}
                  </span>
                  <span className="text-lg font-bold text-blue-600">
                    {opStats.percentage}%
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      opStats.percentage >= 80
                        ? 'bg-green-500'
                        : opStats.percentage >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${opStats.percentage}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{opStats.correct} rigtige</span>
                  <span>{opStats.wrong} forkerte</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Difficulty Breakdown */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="text-orange-600" size={28} />
          <h3 className="text-xl font-bold text-gray-800">
            Præstation pr. sværhedsgrad
          </h3>
        </div>

        <div className="space-y-3">
          {['let', 'medium', 'svært'].map((diff) => {
            const diffStats = getDifficultyAccuracy(diff);
            if (diffStats.total === 0) return null;

            return (
              <div key={diff} className="bg-gray-50 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-700">
                    {difficultyNames[diff]}
                  </span>
                  <span className="text-lg font-bold text-orange-600">
                    {diffStats.percentage}%
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      diffStats.percentage >= 80
                        ? 'bg-green-500'
                        : diffStats.percentage >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${diffStats.percentage}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{diffStats.correct} rigtige</span>
                  <span>{diffStats.wrong} forkerte</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Insights & Suggestions */}
      {(weakest || suggestion) && (
        <div className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Award className="text-yellow-600" size={28} />
            <h3 className="text-xl font-bold text-gray-800">
              Anbefalinger
            </h3>
          </div>

          <div className="space-y-3">
            {weakest && (
              <div className="bg-white rounded-xl p-4">
                <div className="text-sm font-semibold text-orange-600 mb-1">
                  💪 Øv mere på:
                </div>
                <div className="text-gray-800">
                  <span className="font-bold">{operationNames[weakest.operation]}</span>
                  {' '}opgaver ({weakest.percentage}% præcision)
                </div>
              </div>
            )}

            {suggestion && (
              <div className="bg-white rounded-xl p-4">
                <div className="text-sm font-semibold text-blue-600 mb-1">
                  🎯 Forslag:
                </div>
                <div className="text-gray-800">
                  {suggestion.reason}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default StatisticsPanel;
